import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import EmailsPage from "@/pages/EmailsPage";
import CasaPage from "@/pages/CasaPage";
import NaipsPage from "@/pages/NaipsPage";
import NotamPage from "@/pages/NotamPage";
import RiskPage from "@/pages/RiskPage";
import WeatherPage from "@/pages/WeatherPage";
import EarthData from "@/pages/EarthData";
import DampPage from "@/pages/DampPage";
import NotesPage from "@/pages/NotesPage";
import JobDetailPage from "@/pages/JobDetailPage";
import AssetsPage from "@/pages/AssetsPage";
import TrackingPage from "@/pages/TrackingPage";
import PartsPage from "@/pages/PartsPage";
import TodayMapPage from "@/pages/TodayMapPage";
import HeatmapPage from "@/pages/HeatmapPage";
import UsersPage from "@/pages/UsersPage";
import UserDetailPage from "@/pages/UserDetailPage";
import TrainingPage from "@/pages/TrainingPage";
import AssetDetailPage from "@/pages/AssetDetailPage";
import PartDetailPage from "@/pages/PartDetailPage";
import PartEditPage from "@/pages/PartEditPage";
import CartPage from "@/pages/CartPage";
import OrdersPage from "@/pages/OrdersPage";
import JobsPipelinePage from "@/pages/JobsPipelinePage";
import JobEditPage from "@/pages/JobEditPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import SettingsPage from "@/pages/SettingsPage";
import Layout from "@/components/Layout";
import "@/App.css";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const process = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (!sessionId) {
        navigate("/");
        return;
      }
      // Strip the one-time session_id from the URL IMMEDIATELY — before the network
      // exchange — so it isn't visible in the address bar/history during the round-trip.
      window.history.replaceState(null, "", "/dashboard");
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        // Persist the token for the Authorization header fallback (third-party
        // cookie blocking on desktop). Cookie is still set server-side too.
        if (res.data?.session_token) {
          localStorage.setItem("session_token", res.data.session_token);
        }
        setUser(res.data);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed. Please try again.";
        navigate(`/?error=${encodeURIComponent(msg)}`, { replace: true });
      }
    };
    process();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <div className="h-10 w-10 border-4 border-lime-400 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-lime-400 mt-4 font-heading">Signing you in…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

const LAYOUT_ROUTES = [
  { path: "/dashboard", el: <Dashboard /> },
  { path: "/calendar", el: <CalendarPage /> },
  { path: "/emails", el: <EmailsPage /> },
  { path: "/clients", el: <ClientsPage /> },
  { path: "/clients/:id", el: <ClientDetailPage /> },
  { path: "/assets", el: <AssetsPage /> },
  { path: "/tracking", el: <TrackingPage /> },
  { path: "/parts", el: <PartsPage /> },  { path: "/casa", el: <CasaPage /> },
  { path: "/naips", el: <NaipsPage /> },
  { path: "/notam", el: <NotamPage /> },
  { path: "/risk", el: <RiskPage /> },
  { path: "/weather", el: <WeatherPage /> },
  { path: "/earth", el: <EarthData /> },
  { path: "/damp", el: <DampPage /> },
  { path: "/notes", el: <NotesPage /> },
  { path: "/jobs", el: <JobsPipelinePage /> },
  { path: "/jobs/new", el: <JobEditPage /> },
  { path: "/jobs/:id/edit", el: <JobEditPage /> },
  { path: "/jobs/:id", el: <JobDetailPage /> },
  { path: "/map/today", el: <TodayMapPage /> },
  { path: "/map/heatmap", el: <HeatmapPage /> },
  { path: "/training", el: <TrainingPage /> },
  { path: "/assets/:id", el: <AssetDetailPage /> },
  { path: "/parts/new", el: <PartEditPage /> },
  { path: "/parts/:id/edit", el: <PartEditPage /> },
  { path: "/parts/:id", el: <PartDetailPage /> },
  { path: "/cart", el: <CartPage /> },
  { path: "/orders", el: <OrdersPage /> },
  { path: "/orders/:id", el: <OrderDetailPage /> },
  { path: "/users", el: <UsersPage /> },
  { path: "/users/:id", el: <UserDetailPage /> },
  { path: "/settings", el: <SettingsPage /> },
];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      {LAYOUT_ROUTES.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            <ProtectedRoute>
              <Layout>{r.el}</Layout>
            </ProtectedRoute>
          }
        />
      ))}
      <Route
        path="/map/today/fullscreen"
        element={
          <ProtectedRoute>
            <TodayMapPage fullscreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map/heatmap/fullscreen"
        element={
          <ProtectedRoute>
            <HeatmapPage fullscreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/display"
        element={
          <ProtectedRoute>
            <Dashboard display />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
